/**
 * @fileoverview Absolutely-positioned icon button atom.
 * @description A borderless button sized and positioned for one of two fixed placements — a
 * trailing adornment inside a relatively-positioned input wrapper, or a corner dismiss control
 * inside a panel — selected via a variant prop rather than an overridable className, since each
 * placement is a single complete, pre-written position/size/background treatment.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

interface IconButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'type'> {
  'aria-label': string
  variant?: 'input-adornment' | 'modal-close'
}

const VARIANT_CLASS_NAMES: Record<NonNullable<IconButtonProps['variant']>, string> = {
  'input-adornment': 'right-0 top-0 h-[52px] w-[50px]',
  'modal-close': 'right-[17px] top-[17px] h-[34px] w-[34px] rounded-lg bg-surface-muted',
}

/**
 * @component
 * @param {IconButtonProps} props - Standard button attributes plus variant; aria-label is
 * required since the button has no visible text.
 * @param {'input-adornment' | 'modal-close'} [props.variant] - "input-adornment" (default) for the
 * password show/hide toggle; "modal-close" for a panel's corner dismiss control.
 */
function IconButton({ variant = 'input-adornment', children, ...props }: IconButtonProps) {
  return (
    <button
      type="button"
      className={`absolute flex cursor-pointer items-center justify-center ${VARIANT_CLASS_NAMES[variant]}`}
      {...props}
    >
      {children}
    </button>
  )
}

export default IconButton
